import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import WaveDropsEmptyPlaceholder from "@/components/waves/drops/WaveDropsEmptyPlaceholder";
import { useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject, Ref } from "react";
import { WaveDropsMessageListSection } from "./WaveDropsMessageListSection";
import { WaveDropsTypingIndicator } from "./WaveDropsTypingIndicator";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsContentProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly scrollContainerCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly typingMessage: string | null;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
  readonly onScrollToUnread?: ((serialNo: number) => void) | undefined;
  readonly unreadCount?: number | undefined;
  readonly autoCollapseSerials?: ReadonlySet<number> | undefined;
  readonly suspendLightDropHydration?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
export const WaveDropsContent: React.FC<WaveDropsContentProps> = t0 => {
  const $ = _c(41);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    onTopIntersection,
    onReply,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    typingMessage,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    boostedDrops,
    onBoostedDropClick,
    onScrollToUnread,
    unreadCount,
    autoCollapseSerials,
    suspendLightDropHydration: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const suspendLightDropHydration = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const {
    unreadDividerSerialNo,
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  let t4;
  if ($[0] !== setUnreadDividerSerialNo) {
    t4 = () => {
      setUnreadDividerSerialNo(null);
    };
    $[0] = setUnreadDividerSerialNo;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleDismissUnread = t4;
  const dropsCount = waveMessages?.drops.length ?? 0;
  const isInitialLoading = !!waveMessages?.isLoading && !waveMessages.isLoadingNextPage && dropsCount === 0;
  const isHydrating = !waveMessages;
  if (isHydrating || isInitialLoading) {
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    return t5;
  }
  if (dropsCount === 0) {
    let t5;
    if ($[3] !== dropId) {
      t5 = <WaveDropsEmptyPlaceholder dropId={dropId} />;
      $[3] = dropId;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  let t5;
  if ($[5] !== activeDrop || $[6] !== autoCollapseSerials || $[7] !== boostedDrops || $[8] !== bottomAnchorCallbackRef || $[9] !== bottomAnchorRef || $[10] !== bottomPaddingClassName || $[11] !== dropId || $[12] !== handleDismissUnread || $[13] !== isAtBottom || $[14] !== isVotingClosed || $[15] !== isVotingControlsLocked || $[16] !== onBoostedDropClick || $[17] !== onDropContentClick || $[18] !== onQuoteClick || $[19] !== onReply || $[20] !== onRevealPending || $[21] !== onScrollToUnread || $[22] !== onTopIntersection || $[23] !== pendingCount || $[24] !== queueSerialTarget || $[25] !== scrollContainerCallbackRef || $[26] !== scrollContainerRef || $[27] !== scrollToBottom || $[28] !== serialTarget || $[29] !== suspendLightDropHydration || $[30] !== targetDropRef || $[31] !== unreadCount || $[32] !== unreadDividerSerialNo || $[33] !== waveMessages || $[34] !== winningThreshold) {
    t5 = <WaveDropsMessageListSection waveMessages={waveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={onTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} onDropContentClick={onDropContentClick} pendingCount={pendingCount} onRevealPending={onRevealPending} bottomPaddingClassName={bottomPaddingClassName} unreadDividerSerialNo={unreadDividerSerialNo} unreadCount={unreadCount} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} onScrollToUnread={onScrollToUnread} onDismissUnread={handleDismissUnread} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={suspendLightDropHydration} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[5] = activeDrop;
    $[6] = autoCollapseSerials;
    $[7] = boostedDrops;
    $[8] = bottomAnchorCallbackRef;
    $[9] = bottomAnchorRef;
    $[10] = bottomPaddingClassName;
    $[11] = dropId;
    $[12] = handleDismissUnread;
    $[13] = isAtBottom;
    $[14] = isVotingClosed;
    $[15] = isVotingControlsLocked;
    $[16] = onBoostedDropClick;
    $[17] = onDropContentClick;
    $[18] = onQuoteClick;
    $[19] = onReply;
    $[20] = onRevealPending;
    $[21] = onScrollToUnread;
    $[22] = onTopIntersection;
    $[23] = pendingCount;
    $[24] = queueSerialTarget;
    $[25] = scrollContainerCallbackRef;
    $[26] = scrollContainerRef;
    $[27] = scrollToBottom;
    $[28] = serialTarget;
    $[29] = suspendLightDropHydration;
    $[30] = targetDropRef;
    $[31] = unreadCount;
    $[32] = unreadDividerSerialNo;
    $[33] = waveMessages;
    $[34] = winningThreshold;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== typingMessage) {
    t6 = <WaveDropsTypingIndicator typingMessage={typingMessage} />;
    $[36] = typingMessage;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t5 || $[39] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  return t7;
};